import React from 'react';
import { useWikiV2Client } from '../../../hooks/useWikiV2';
import WikiIcon from '../shared/WikiIcon';
import { ASK_COPY, FEEDBACK_REASONS } from './askCopy';
import { askStore, useAskStore } from './askStore';

/**
 * 👍 / 👎 (D-ask §1.3(4)) and the 👎 panel (§1.3(5)). 👍 records the vote at
 * once; 👎 opens "What went wrong?" (one panel at a time) and the vote is
 * recorded by "Send feedback". Votes are final.
 */

export function VoteButtons({ turn }) {
    const client = useWikiV2Client();
    const busy = useAskStore((s) => s.busy[turn.turn_id] || null);
    const rating = turn.feedback?.rating || null;
    const locked = Boolean(rating) || turn.can_feedback === false || Boolean(busy);
    const label = rating
        ? (turn.feedback_label || (rating === 'up' ? ASK_COPY.markedHelpful : ASK_COPY.lessonSaved))
        : null;

    return (
        <>
            <button
                type="button"
                className={`wk-ask-thumb ${rating === 'up' ? 'is-up' : ''}`.trim()}
                title={ASK_COPY.helpful}
                aria-label={ASK_COPY.helpful}
                aria-pressed={rating === 'up'}
                aria-disabled={locked || undefined}
                onClick={() => { if (!locked) askStore.vote(client, turn.turn_id, 'up'); }}
                data-testid="wiki-ask-up"
            >
                <WikiIcon name="thumbs-up" size={15} />
            </button>
            <button
                type="button"
                className={`wk-ask-thumb ${rating === 'down' ? 'is-down' : ''}`.trim()}
                title={ASK_COPY.notHelpful}
                aria-label={ASK_COPY.notHelpful}
                aria-pressed={rating === 'down'}
                aria-disabled={locked || undefined}
                onClick={() => { if (!locked) askStore.openFeedback(turn.turn_id); }}
                data-testid="wiki-ask-down"
            >
                <WikiIcon name="thumbs-down" size={15} />
            </button>
            {label && <span className="wk-ask-voted" data-testid="wiki-ask-voted">{label}</span>}
        </>
    );
}

export function FeedbackPanel({ turnId }) {
    const client = useWikiV2Client();
    const reason = useAskStore((s) => s.fbReason);
    const text = useAskStore((s) => s.fbText);
    const textId = `wk-ask-fb-${turnId}`;

    return (
        <div className="wk-ask-fb" data-testid="wiki-ask-feedback">
            <div className="wk-ask-fb-title" id={`${textId}-title`}>{ASK_COPY.whatWentWrong}</div>
            <div className="wk-ask-fb-reasons" role="group" aria-labelledby={`${textId}-title`}>
                {FEEDBACK_REASONS.map((option) => (
                    <button
                        key={option.value}
                        type="button"
                        className={`wk-ask-chip ${reason === option.value ? 'is-selected' : ''}`.trim()}
                        aria-pressed={reason === option.value}
                        onClick={() => askStore.setFeedbackReason(option.value)}
                        data-reason={option.value}
                    >
                        {option.label}
                    </button>
                ))}
            </div>
            <textarea
                id={textId}
                className="wk-ask-fb-text"
                value={text}
                maxLength={2000}
                placeholder={ASK_COPY.feedbackPlaceholder}
                aria-label={ASK_COPY.feedbackPlaceholder}
                onChange={(event) => askStore.setFeedbackText(event.target.value)}
            />
            <div className="wk-ask-fb-foot">
                <span className="wk-ask-fb-helper">{ASK_COPY.feedbackHelper}</span>
                <button type="button" className="wk-btn wk-btn--ghost wk-btn--sm" onClick={() => askStore.cancelFeedback()}>
                    {ASK_COPY.cancel}
                </button>
                <button
                    type="button"
                    className="wk-btn wk-btn--primary wk-btn--sm"
                    onClick={() => askStore.vote(client, turnId, 'down')}
                    data-testid="wiki-ask-send-feedback"
                >
                    {ASK_COPY.sendFeedback}
                </button>
            </div>
        </div>
    );
}
